import { FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSignUp } from "./useSignUp";
import { AuthShell } from "./AuthShell";
import { Field } from "../ui/Field";
import { TextInput } from "../ui/TextInput";
import { Button } from "../ui/Button";
import { Banner } from "../ui/Banner";
import { pickFieldError } from "../lib/form";
import { useTranslation, apiErrorMessage } from "../i18n";

export function SignUpPage() {
  const { t } = useTranslation("auth");
  const { t: tErr } = useTranslation("errors");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [secretWord, setSecretWord] = useState("");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Honour ?next (e.g. /sign-up?next=/invite/<token>) so "Sign up to accept" an
  // invite returns to the invite instead of dead-ending on the workspace list
  // (bugloop L3) — matching SignInPage.
  const next = params.get("next") ?? "/";
  const signUp = useSignUp();

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    signUp.mutate(
      { email, password, displayName, secretWord },
      { onSuccess: () => void navigate(next, { replace: true }) },
    );
  }

  const nameError = pickFieldError(signUp.error, "displayName");
  const emailError = pickFieldError(signUp.error, "email");
  const passwordError = pickFieldError(signUp.error, "password");
  const genericError =
    signUp.error && signUp.error.code !== "validation_failed"
      ? // On sign-up a `conflict` is specifically a taken email — show that
        // rather than the generic "already exists".
        signUp.error.code === "conflict"
        ? tErr("emailTaken")
        : apiErrorMessage(signUp.error, tErr)
      : undefined;

  return (
    <AuthShell>
      <h1 className="mb-6 text-2xl font-semibold text-slate-100">{t("signUpTitle")}</h1>

      {genericError ? (
        <Banner kind="error" className="mb-4">
          {genericError}
        </Banner>
      ) : null}

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Field label={t("displayName")} error={nameError}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              type="text"
              autoComplete="name"
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              hasError={!!nameError}
              {...ariaProps}
            />
          )}
        </Field>

        <Field label={t("email")} error={emailError}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              hasError={!!emailError}
              {...ariaProps}
            />
          )}
        </Field>

        <Field label={t("password")} error={passwordError}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              hasError={!!passwordError}
              {...ariaProps}
            />
          )}
        </Field>

        <Field label={t("secretWord")}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              type="text"
              autoComplete="off"
              required
              value={secretWord}
              onChange={(e) => setSecretWord(e.target.value)}
              {...ariaProps}
            />
          )}
        </Field>

        <Button
          type="submit"
          variant="primary"
          className="w-full"
          disabled={signUp.isPending}
        >
          {signUp.isPending ? t("creatingAccount") : t("createAccount")}
        </Button>
      </form>

      <p className="mt-6 text-sm text-slate-400">
        {t("haveAccount")}{" "}
        <Link to="/sign-in" className="text-arch-400 hover:underline">
          {t("signIn")}
        </Link>
      </p>
    </AuthShell>
  );
}
